import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';

import {
  ButtonComponent, FieldComponent, InputComponent, ModalComponent,
} from '@echospaces-web-monorepo/es-shared-ui';

/**
 * URL-safe slug — mirrors the backend's `zSlug`: lowercase letters,
 * digits, and dashes; no leading/trailing dash, no double dashes.
 */
const SLUG_RE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Modal for creating a new Island (top-level Space). Only org Super-Admins
 * see the "+ New Island" trigger — server-side `CASL manage Island` enforces
 * that, but the SPA hides the button for non-admins as a UX courtesy.
 *
 * Slug is auto-derived from the name as the user types — kebab-cased, ASCII
 * only — but the field is editable so admins can pick a vanity slug. Once
 * the user has touched the slug field, auto-derivation stops to avoid
 * clobbering their input.
 */
@Component({
  selector: 'es-create-island-modal',
  imports: [FormsModule, ModalComponent, ButtonComponent, FieldComponent, InputComponent],
  template: `
    <es-modal [open]="open" title="New Island" width="sm" (closed)="onCancel()">
      <div class="p-6 space-y-4">
        <es-field label="Name" [required]="true" [error]="nameError()">
          <es-input
            placeholder="Welcome World"
            [(ngModel)]="name"
            (ngModelChange)="onNameChange()"
          ></es-input>
        </es-field>

        <es-field
          label="Slug"
          hint="URL identifier — lowercase letters, digits, and dashes."
          [required]="true"
          [error]="slugError()"
        >
          <es-input
            placeholder="welcome-world"
            [(ngModel)]="slug"
            (ngModelChange)="onSlugChange()"
          ></es-input>
        </es-field>

        <es-field label="Description" [required]="true" [error]="descriptionError()">
          <es-input
            placeholder="A welcoming entry hub for first-time visitors."
            [(ngModel)]="description"
          ></es-input>
        </es-field>

        @if (submitError()) {
          <p class="text-sm text-red-400">{{ submitError() }}</p>
        }

        <div class="flex justify-end gap-2 pt-2">
          <es-button variant="ghost" (clicked)="onCancel()">Cancel</es-button>
          <es-button
            variant="primary"
            [disabled]="submitting()"
            (clicked)="onSubmit()"
          >
            {{ submitting() ? 'Creating…' : 'Create Island' }}
          </es-button>
        </div>
      </div>
    </es-modal>
  `,
})
export class CreateIslandModalComponent {
  @Input() open = false;

  @Output() closed = new EventEmitter<void>();
  @Output() submitted = new EventEmitter<{
    name: string;
    slug: string;
    description: string;
  }>();

  name = '';
  slug = '';
  description = '';

  /** Slug auto-derives from name until the user types in the slug field. */
  private slugTouched = false;

  readonly submitting = signal(false);
  readonly nameError = signal<string | null>(null);
  readonly slugError = signal<string | null>(null);
  readonly descriptionError = signal<string | null>(null);
  readonly submitError = signal<string | null>(null);

  onNameChange(): void {
    if (!this.slugTouched) {
      this.slug = this.slugify(this.name);
    }
  }

  onSlugChange(): void {
    this.slugTouched = true;
  }

  onSubmit(): void {
    const name = this.name.trim();
    const slug = this.slug.trim();
    const description = this.description.trim();

    this.nameError.set(name ? null : 'Required');
    this.slugError.set(
      !slug
        ? 'Required'
        : SLUG_RE.test(slug)
          ? null
          : 'Lowercase letters, digits, and single dashes only',
    );
    this.descriptionError.set(description ? null : 'Required');

    if (this.nameError() || this.slugError() || this.descriptionError()) return;

    this.submitting.set(true);
    this.submitted.emit({ name, slug, description });
  }

  /**
   * Parent calls after `submitted` finishes — success resets the form
   * + closes; failure surfaces the error and keeps state.
   */
  finishSubmit(error?: string): void {
    this.submitting.set(false);
    this.submitError.set(error ?? null);
    if (!error) {
      this.name = '';
      this.slug = '';
      this.description = '';
      this.slugTouched = false;
      this.closed.emit();
    }
  }

  onCancel(): void {
    this.nameError.set(null);
    this.slugError.set(null);
    this.descriptionError.set(null);
    this.submitError.set(null);
    this.closed.emit();
  }

  /**
   * ASCII-only kebab-case slugger. Strips accents via NFKD normalize,
   * lowercases, replaces non-alphanumeric with dashes, and collapses
   * + trims dash runs. Matches `SLUG_RE` for short inputs; very long
   * names may need manual edits.
   */
  private slugify(value: string): string {
    return value
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }
}
